import React from 'react';
import type { CategoryInfo } from '@/types/pim';

import AttributeFilters from './InventoryList.AttributeFilters';
import DisableWhileNavigating from './DisableWhileNavigating';
import { useInventoryNav } from './InventoryNavContext';
import { useCatalogResults } from './InventoryList.ResultsContext';

interface MobileFilterMenuProps {
    isFilterOpening: boolean;
    isFilterClosing: boolean;
    closeMobileFilter: () => void;
    clearAllFilters: () => void;
    activeFilterCount: number;
    selectedCategory: CategoryInfo | null;
    /** Committed attribute filters (from the URL). */
    attributeFilters: Record<string, any>;
    /** Immediate-apply handler — each selection navigates, like the desktop sidebar. */
    onFilterChange: (attributeName: string, value: any) => void;
    isOpen: boolean;
}

const MobileFilterMenu: React.FC<MobileFilterMenuProps> = ({
    isFilterOpening,
    isFilterClosing,
    closeMobileFilter,
    clearAllFilters,
    activeFilterCount,
    selectedCategory,
    attributeFilters,
    onFilterChange,
    isOpen
}) => {
    const { isNavigating } = useInventoryNav();
    // Live total for the current filters (from the streamed grid via context),
    // shown on the "Show results" button. Null until the grid publishes.
    const { total } = useCatalogResults();

    const scrollToAttributeFilters = () => {
        const element = document.getElementById('attribute-filters');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (!isOpen && !isFilterClosing) {
        return <div className="hidden" aria-hidden="true" />;
    }

    return (
        <div
            className={`lg:hidden fixed inset-0 z-300 bg-black/30 transition-opacity min-h-dvh duration-300 ${isFilterClosing ? 'opacity-0' : 'opacity-100'}`}
            onClick={closeMobileFilter}
            style={{ touchAction: 'none' }}
        >
            <div
                className={`absolute top-[30%] bottom-0 w-full overflow-y-hidden rounded-t-lg transform transition-transform duration-300 ease-out ${isFilterClosing ? 'translate-y-full' : isFilterOpening ? 'translate-y-full' : 'translate-y-0'}`}
                onClick={(e) => e.stopPropagation()}
                style={{ touchAction: 'auto' }}
            >
                <div className="bg-white shadow-xl h-full p-safe-area-inset overflow-y-hidden">
                    <div className="flex flex-col h-full p-4 justify-center items-center">
                        <div className="flex items-center justify-between mb-4 w-full">
                            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                            <button
                                onClick={closeMobileFilter}
                                className="text-gray-600 border border-gray-400 active:bg-gray-50 active:scale-[0.98] px-4 py-1.25 rounded-xl"
                            >
                                Close
                            </button>
                        </div>

                        <div className="flex w-full justify-between gap-5 items-center">
                            <button
                                disabled={activeFilterCount === 0 || isNavigating}
                                onClick={clearAllFilters}
                                className={`w-full mb-4 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${activeFilterCount === 0 || isNavigating
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'text-red-600 border border-red-500 hover:bg-red-100 active:bg-red-200 active:scale-[0.98]'
                                    }`}
                            >
                                Clear all filters
                            </button>
                            <button
                                disabled={selectedCategory === null}
                                onClick={scrollToAttributeFilters}
                                className={`w-full mb-4 px-4 py-2 text-sm rounded-xl transition-all duration-200 ${selectedCategory === null
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'text-gray-600 border border-gray-300 hover:bg-gray-50 active:bg-gray-50 active:scale-[0.98]'
                                    }`}
                            >
                                Show attribute filters
                            </button>
                        </div>


                        <div className="overflow-y-auto h-fit p-safe-area-inset flex-1 w-full pb-6">

                            {/* Category filter */}
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3">Category</h3>
                            </div>

                            {/* Attribute filters — apply immediately so counts/availability stay live */}
                            {selectedCategory && (
                                <div id="attribute-filters" className="mb-6">
                                    <DisableWhileNavigating>
                                        <AttributeFilters
                                            category={selectedCategory || null}
                                            filters={attributeFilters}
                                            onFilterChange={onFilterChange}
                                        />
                                    </DisableWhileNavigating>
                                </div>
                            )}

                            {!selectedCategory && (
                                <div className="text-sm text-gray-500 italic px-3 mb-5">
                                    Select a category to filter by attributes
                                </div>
                            )}

                        </div>

                        <button onClick={closeMobileFilter} className="px-4 py-2 rounded-xl border border-neutral-400 w-fit mt-6">
                            Show results{total !== null ? ` (${total.toLocaleString()})` : ''}
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileFilterMenu;
